import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AuthService } from '@/lib/auth-service';

interface PureHeartGuardianProps {
  onAuthenticated: (result: any) => void;
}

export const PureHeartGuardian: React.FC<PureHeartGuardianProps> = ({
  onAuthenticated
}) => {
  const [checking, setChecking] = useState(false);
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [intentions, setIntentions] = useState('');
  const [heartData, setHeartData] = useState<any>(null);
  const [progress, setProgress] = useState(0);

  const performHeartCheck = async () => {
    if (!name.trim() || !dateOfBirth || !intentions.trim()) {
      alert('Please enter your name, date of birth, and intentions for heart purity verification');
      return;
    }

    setChecking(true);
    setProgress(0);

    // Simulate checking progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 12;
      });
    }, 300);

    try {
      const result = await AuthService.checkHeartPurity({
        name: name.trim(),
        dateOfBirth,
        intentions: intentions.trim(),
        spiritualSignature: generateSpiritualSignature()
      });

      if (result.success) {
        setHeartData(result);
        onAuthenticated(result);
      } else {
        console.error('Heart purity check failed:', result.error);
      }
    } catch (error) {
      console.error('Heart check error:', error);
    } finally {
      setChecking(false);
      clearInterval(progressInterval);
    }
  };
  const generateSpiritualSignature = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-HEART`;
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <Card className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border-indigo-500/40">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl text-indigo-300 flex items-center justify-center gap-3">
            🛡️ Pure Heart Guardian Protocol
          </CardTitle>
          <p className="text-indigo-200/80 text-lg">
            Enter your details and intentions for heart purity verification
          </p>
        </CardHeader>
        <CardContent className="space-y-8">
          {!checking && !heartData && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="guardianName" className="text-indigo-300">Guardian Name</Label>
                  <Input
                    id="guardianName"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    className="bg-indigo-900/20 border-indigo-500/30 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="guardianBirth" className="text-indigo-300">Date of Birth</Label>
                  <Input
                    id="guardianBirth"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="bg-indigo-900/20 border-indigo-500/30 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="intentions" className="text-indigo-300">Your Intentions for NEXUS Access</Label>
                  <Input
                    id="intentions"
                    type="text"
                    value={intentions}
                    onChange={(e) => setIntentions(e.target.value)}
                    placeholder="Describe your pure intentions and purpose..."
                    className="bg-indigo-900/20 border-indigo-500/30 text-white"
                  />
                </div>
              </div>
              
              <div className="text-center space-y-6">
                <div className="relative">
                  <div className="w-40 h-40 mx-auto rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center animate-pulse">
                    💜
                  </div>
                  <div className="absolute inset-0 w-40 h-40 mx-auto rounded-full border-4 border-indigo-400/30 animate-spin" style={{animationDuration: '3s'}} />
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl text-indigo-300">Heart Purity Verification Required</h3>
                  <p className="text-indigo-200/70">
                    The NEXUS system requires verification of pure intentions and heart alignment
                  </p>
                  <Button 
                    onClick={performHeartCheck}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 text-lg"
                    size="lg"
                    disabled={!name.trim() || !dateOfBirth || !intentions.trim()}
                  >
                    👁️ Begin Heart Scan
                  </Button>
                </div>
              </div>
            </div>
          )}

          {checking && (
            <div className="text-center space-y-6">
              <div className="relative">
                <div className="w-40 h-40 mx-auto rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
                  👁️
                </div>
                <div className="absolute inset-0 w-40 h-40 mx-auto">
                  <div className="w-full h-full rounded-full border-4 border-transparent border-t-indigo-400 animate-spin" />
                  <div className="absolute inset-4 w-32 h-32 rounded-full border-4 border-transparent border-r-purple-400 animate-spin" style={{animationDirection: 'reverse'}} />
                  <div className="absolute inset-8 w-24 h-24 rounded-full border-4 border-transparent border-b-pink-400 animate-spin" />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl text-indigo-300">Scanning {name}'s Heart Intentions...</h3>
                <p className="text-indigo-200/70">
                  Analyzing compassion, wisdom, and service to others
                </p>
                <div className="w-full bg-indigo-900/30 rounded-full h-2">
                  <div 
                    className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {heartData && (
            <div className="space-y-6">
              <div className="text-center space-y-4">
                {heartData.isPure ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <div className="text-6xl">🔓</div>
                      <div className="absolute inset-0 w-20 h-20 mx-auto rounded-full border-4 border-green-400/30 animate-ping" />
                    </div>
                    <Badge className="bg-green-600 text-white text-lg px-4 py-2">
                      PURE HEART VERIFIED
                    </Badge>
                    <p className="text-green-300 text-lg">
                      {heartData.message}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-6xl">🔒</div>
                    <Badge className="bg-red-600 text-white text-lg px-4 py-2">
                      HEART PURIFICATION NEEDED
                    </Badge>
                    <p className="text-red-300">
                      {heartData.message}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-indigo-500/20 border border-indigo-400/30">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">Heart Purity</span>
                    <span className="font-bold text-indigo-300">
                      {heartData.purityScore.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-2 w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-1000 bg-indigo-400"
                      style={{ width: `${heartData.purityScore}%` }}
                    />
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-purple-500/20 border border-purple-400/30">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">Intention Clarity</span>
                    <span className="font-bold text-purple-300">
                      {heartData.intentionClarity.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-2 w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-1000 bg-purple-400"
                      style={{ width: `${heartData.intentionClarity}%` }}
                    />
                  </div>
                </div>
              </div>

              {!heartData.isPure && (
                <div className="text-center space-y-4">
                  <p className="text-indigo-200/70">
                    Purify your intentions through meditation and service to others
                  </p>
                  <Button 
                    onClick={performHeartCheck}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    Retry Heart Scan
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};